<script lang="ts">
    import {Button, Card, CardActions, CardSubtitle, CardText, CardTitle, Icon} from "svelte-materialify";
    import {Checkbox, Table} from "svelte-materialify/src";
    import type {Meeting} from '../firestore';
    import {postAttendance, removeAttendee} from '../firestore';
    import SeatChip from './SeatChip.svelte';
    import {error, feedback, user} from "../stores";
    import {isUserAdmin, meetingSeatAssignments} from "../util";
    import xor from 'lodash/xor';
    import MemberComponent from './Member.svelte';
    import SeatPicker from "./SeatPicker.svelte";
    import { slide } from 'svelte/transition';

    export let value: Meeting;
    $: admin = $user != null && isUserAdmin($user);

    let currentAttending = value.attendees
        .filter(it => it.present)
        .map(it => it.identity.email);
    let attending: string[] = [...currentAttending];

    $: localModified = xor(currentAttending, attending).length > 0;

    function clickPostAttendance() {
        const presence = Object.fromEntries(value.attendees.map(it => [it.identity.email, attending.includes(it.identity.email)]));
        postAttendance(value.id, presence)
            .then(() => {
                feedback.set(`Attendance posted for ${value.id}`);
                currentAttending = [...attending];
            })
            .catch(error.set);
    }

</script>

<Card>
    <div style="display: flex; justify-content: space-between;">
        <div>
            <CardTitle>
                <h5>{value.id}</h5>
            </CardTitle>
            <CardSubtitle>
                {value.startTime.toLocaleTimeString()}
                to
                {value.endTime.toLocaleTimeString()}
            </CardSubtitle>
        </div>
        <div
                style="padding: 1rem; font-size: 1.4rem;"
                class="grey-text text-darken-2">
            {value.attendees.length}
            /
            {value.capacity}
        </div>
    </div>
    <CardText>
        {#if value.attendees.length > 0}
          <div transition:slide>
            <SeatPicker assignments={meetingSeatAssignments(value)} selectable={false}/>
          </div>
            <Table dense style="overflow:hidden">
                <thead>
                <tr>
                    <th>Name</th>
                    <th>Seat</th>
                    <th>Goal</th>
                    <th>Present</th>
                    {#if admin}
                        <th>Remove</th>
                    {/if}
                </tr>
                </thead>
                <tbody>
                {#each value.attendees as person (person.identity.email)}
                    <tr>
                        <td>
                            <MemberComponent value={person.identity}/>
                        </td>
                        <td>
                            <SeatChip value={person.seat}/>
                        </td>
                        <td>{person.goal}</td>
                        <td>
                            {#if admin}
                                <Checkbox
                                        disabled={!admin}
                                        bind:group={attending}
                                        value={person.identity.email}/>
                            {:else if person.present}
                                <Icon class="material-icons">check_box</Icon>
                            {:else}
                                <Icon class="material-icons">check_box_outline_blank</Icon>
                            {/if}
                        </td>
                        {#if admin}
                            <td class="remove-member">
                                <Button icon size="small"
                                        on:click={() => removeAttendee(value.id, person.identity.email)}>
                                    <span class="material-icons">close</span>
                                </Button>
                            </td>
                        {/if}
                    </tr>
                {/each}
                </tbody>
            </Table>
        {:else}
            <i>No members attending</i>
        {/if}
    </CardText>
    {#if localModified}
        <div transition:slide>
            <CardActions>
                <Button text class="primary-text" on:click={clickPostAttendance}>
                    <Icon class="material-icons">send</Icon>&nbsp;
                    Post Attendance
                </Button>
            </CardActions>
        </div>
    {/if}
</Card>

<style lang="scss">
  @import "svelte-materialify/src/styles/tools/colors";

  .remove-member {
    color: material-color("red", "darken-3");
  }
</style>